
































































































































































































import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import { treatError } from '@/utils';
import UploadService from '@/api/ImageUploadService';
import TextInput from '@/components/TextInput/TextInput.vue';
import FlexButton from '@/components/FlexButton/FlexButton.vue';
import ModalOptions from '@/components/Modal/ModalOptions';
import eventBus from '../../utils/eventBus';
import { IImageData } from './ImageData';
import loadImage from 'blueimp-load-image';
import Typography from '@/components/atoms/Typography/Typography.vue';
import FilledButton from '@/components/molecules/FilledButton/FilledButton.vue';
import Thumb from '@/components/molecules/Thumb/Thumb.vue';
import UploadIcon from '@/components/icons/Upload.vue';
import CloseCircleIcon from '@/components/icons/CloseCircle.vue';

@Component({
  components: {
    TextInput,
    FlexButton,
    Typography,
    FilledButton,
    Thumb,
    UploadIcon,
    CloseCircleIcon
  }
})
export default class ImageUpload extends Vue {
  @Prop({ default: false }) error!: boolean;
  @Prop({ default: 'small-circle' }) type!: string;
  @Prop({ default: false }) dropfull!: string | boolean;
  @Prop({ default: true }) cleanOnExit!: boolean;
  @Prop({ default: false }) src!: string;
  @Prop({ default: '' }) title!: string;
  @Prop({ default: 'large' }) size!: string;
  @Prop({ default: false }) waitData?: boolean;
  @Prop({ default: null }) id?: string;
  @Prop({ default: false }) cropEnabled!: boolean;
  @Prop({ default: false }) highlightError!: boolean;
  @Prop({ default: false }) uploadButton!: boolean;
  @Prop({ default: 'primary' }) colorSchema!: 'primary' | 'secondary';
  @Prop({ default: true }) cropShouldScaleImage!: boolean;
  @Prop({ default: '' }) cropTitle!: string;
  @Prop({ default: null }) returnTo?: [string, ...any[]];
  @Prop({ default: false }) forceFull?: boolean;
  @Prop({ default: false }) disableModalBehaviourMobile?: boolean;
  @Prop({ default: true }) emitTempImageBeforeUpload?: boolean;
  @Prop({ default: false }) insideLoader?: boolean;
  @Prop({ default: false }) setLoading?: boolean;
  @Prop({ default: false }) disableDrop?: boolean;
  @Prop({ default: false }) showLoaderOnSaveEditMode!: boolean;
  @Prop({ default: '' }) buttonLabelText?: boolean;
  @Prop({ default: false }) showUploadIcon?: boolean;
  @Prop({ default: '' }) disclaimerText?: string;
  @Prop({ default: false })
  roundedCropWrapper?: boolean;
  @Prop({ default: '' }) gaEventCategory?: string;

  service: UploadService = new UploadService();

  cropMinZoom: number = 1;
  previousOriginalFile: string = '';
  originalFile: string = '';
  file: string = '';
  showDragText: boolean = false;
  loading: boolean = false;
  initialCropState: any = null;
  imageId: string = '';

  boundaryOptions =
    this.size === 'wideLarge'
      ? {
          width: window.innerWidth > 767 ? 840 : window.innerWidth - 20,
          height:
            window.innerWidth > 767 ? 350 : (window.innerWidth - 20) * 0.41
        }
      : this.getSize();

  viewportOptions = {
    ...this.boundaryOptions,
    type: this.type.match(/circle/) ? 'circle' : 'square'
  };

  options = {
    type: 'blob',
    size: 'original',
    circle: false,
    quality: 0.85,
    format: 'jpeg'
  };

  @Watch('src')
  onSrc(src: string, oldSrc: string | null) {
    if (this.waitData && oldSrc === null && src !== null) {
      this.loading = false;
    }
  }

  get tPrefix() {
    return 'common.components.imageUpload';
  }

  getSize() {
    let size = 352;

    if (window.innerHeight < 698 || window.innerWidth < 768) {
      size = 200;
    }

    return {
      width: size,
      height: size
    };
  }

  created() {
    if (this.waitData) {
      this.loading = this.waitData;
    }

    this.bindEvents();
  }

  get buttonLabel() {
    if (this.src) return this.$t(`${this.tPrefix}.buttons.buttonLabelText.src`);

    if (!this.buttonLabelText)
      return this.$t(`${this.tPrefix}.buttons.buttonLabelText.default`);

    return this.buttonLabelText;
  }

  get cropTitleLabel() {
    if (!this.cropTitle) return this.$t(`${this.tPrefix}.cropTitleDefault`);

    return this.cropTitle;
  }

  get disclaimerLabel() {
    if (!this.disclaimerText)
      return this.$t(`${this.tPrefix}.disclaimerTextDefault`);

    return this.disclaimerText;
  }

  get buttonColorSchema() {
    const colorSchemaMap: Record<string, string> = {
      dashed: 'gradient'
    };

    if (!colorSchemaMap[this.type]) return 'secondary';

    return colorSchemaMap[this.type];
  }

  openFileInput() {
    const inputFile = this.$refs.inputfile as any;
    inputFile.click();

    if (this.gaEventCategory) {
      this.$analytics({
        eventCategory: this.gaEventCategory,
        eventLabel: `Clicked on the "${this.buttonLabel}" button`,
        eventAction: 'click'
      });
    }
  }

  getIconClass() {
    if (this.type === 'small-placeholder') {
      return 'icon-image-placeholder';
    }

    if (this.type === 'small-button' || this.type.includes('large')) {
      return 'icon-cloud-upload-v2';
    }

    return `icon-cloud-upload-${
      this.colorSchema === 'secondary' ? 'black' : 'pink'
    }`;
  }

  getClassNames() {
    const classNames: any = {
      'input-image-error': this.error,
      'input-image-error-highlight': this.error && this.highlightError
    };

    if (this.dropfull) {
      classNames[`-dropfull${this.dropfull}`] = true;
    }

    classNames[`-${this.type}`] = true;

    return classNames;
  }

  saveEditMode({ img, cropState, original }: any) {
    this.loading = true;
    this.$emit('onLoading');
    if (this.showLoaderOnSaveEditMode && this.src) {
      this.$showLoaderWithLocker();
    }
    this.initialCropState = cropState;
    const output = this.dataURItoBlob(img);

    if (original && this.returnTo) {
      this.originalFile = original;
    }

    if (this.emitTempImageBeforeUpload) {
      this.$emit('onDone', { link: output });
    }

    this.saveImage(output);
  }

  enterEditMode() {
    if (!this.cropEnabled) return;

    const {
      initialCropState,
      boundaryOptions,
      viewportOptions,
      saveEditMode,
      onCancelEditMode,
      imageId,
      options,
      cropTitleLabel
    } = this;

    const actualImage = this.src;
    const src = this.originalFile;

    eventBus.$emit(
      'openSubModal',
      new ModalOptions(
        ``,
        ``,
        'none',
        true,
        ``,
        ``,
        ``,
        false,
        'cropModal',
        false,
        true,
        true,
        false,
        true,
        null,
        this.disableModalBehaviourMobile
      ),
      {
        initialState: initialCropState,
        onSave: saveEditMode,
        onCancel: onCancelEditMode,
        boundaryOptions,
        viewportOptions,
        options,
        minZoom: String(this.cropMinZoom),
        greenButton: this.colorSchema === 'secondary',
        scaleImage: this.cropShouldScaleImage,
        src,
        title: cropTitleLabel,
        actualImage,
        imageId,
        roundedCropWrapper: this.roundedCropWrapper,
        returnTo: this.returnTo
      }
    );
  }

  onCancelEditMode({ img, cropState, imageId, original }: any) {
    this.loading = false;

    if (this.returnTo) {
      const output = img;
      this.initialCropState = cropState;
      this.originalFile = original;
      this.imageId = imageId;

      this.$emit('upload', {
        id: imageId,
        publicPath: { large: output, small: output }
      });
      this.$emit('onDone', { link: output });
    } else {
      this.originalFile = String(this.previousOriginalFile);
    }
  }

  mounted() {
    eventBus.$on('cleanUploadPhoto', this.resetData);
  }

  beforeDestroy() {
    this.unBindEvents();
  }

  bindEvents() {
    eventBus.$on('cropmodal-onSave', this.saveEditMode);
    eventBus.$on('cropmodal-onCancel', this.onCancelEditMode);
    eventBus.$on('custom.drag', this.drag);
    eventBus.$on('saveImage', (src: any, width: number, height: number) => {
      const minDimesion =
        this.size === 'wideLarge' ? width : Math.min(width, height);
      this.cropMinZoom = this.boundaryOptions.width / minDimesion;

      this.originalFile = src;
      this.enterEditMode();
    });
  }

  drag(status: boolean, event: any) {
    if (!this.disableDrop) {
      this.showDragText = status;
    }
  }

  unBindEvents() {
    eventBus.$off('drag');
    eventBus.$off('cleanUploadPhoto', this.resetData);
    eventBus.$off('cropmodal-onSave', this.saveEditMode);
    eventBus.$off('cropmodal-onCancel', this.onCancelEditMode);
  }

  upload(data: FormData) {
    let dataToSend;
    this.service
      .upload(data)
      .then(res => {
        const data: IImageData = res.data;
        const image = res.data.publicPath[this.size];
        dataToSend = data.id;
        this.imageId = data.id;
        this.$emit('upload', data);
        this.$emit('onDone', { id: data.id, link: image });
      })
      .catch(err => {
        this.$notification(treatError(err));
      })
      .finally(() => {
        this.loading = false;
      });
  }

  dataURItoBlob(dataURI: string) {
    // convert base64 to raw binary data held in a string
    // doesn't handle URLEncoded DataURIs - see SO answer #6850276 for code that does this
    var byteString = atob(dataURI.split(',')[1]);

    // separate out the mime component
    var mimeString = dataURI
      .split(',')[0]
      .split(':')[1]
      .split(';')[0];

    // write the bytes of the string to an ArrayBuffer
    var ab = new ArrayBuffer(byteString.length);

    // create a view into the buffer
    var ia = new Uint8Array(ab);

    // set the bytes of the buffer to the correct values
    for (var i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i);
    }

    // write the ArrayBuffer to a blob, and you're done
    var blob = new Blob([ab], { type: mimeString });
    return blob;
  }

  saveImage(actualFile: any) {
    this.previousOriginalFile = String(this.originalFile);

    const fd = new FormData();
    const file = actualFile;
    fd.append('data', file);
    this.upload(fd);
  }

  dropPicture(event: any) {
    this.pictureUpload(event);
  }

  pictureUpload(event: any) {
    const file = event.target.files[0];
    if (!file) return;
    if (
      !file.type.includes('jpeg') &&
      !file.type.includes('png') &&
      !file.type.includes('jpg')
    ) {
      this.$notification(
        String(this.$t(`${this.tPrefix}.warnNotification`)),
        'warn'
      );
      return;
    }

    this.$showLoaderWithLocker();
    this.initialCropState = null;

    loadImage(
      file,
      async (canvas: HTMLCanvasElement) => {
        const link = canvas.toDataURL('image/png', 1);
        const i = new Image();
        i.onload = () => {
          const minDimesion =
            this.size === 'wideLarge' ? i.width : Math.min(i.width, i.height);
          this.cropMinZoom = this.boundaryOptions.width / minDimesion;

          if (!this.cropEnabled) {
            this.originalFile = '';
            this.loading = true;
            this.$emit('onLoading');
            this.saveImage(file);
          } else {
            this.originalFile = link;
            this.enterEditMode();
          }
          this.$hideLoader();
        };
        i.src = link;
      },
      { canvas: true, meta: true, orientation: true }
    );
  }

  resetData() {
    this.showDragText = false;
    this.loading = false;
  }
}
